import optimizely, { NotificationCenter } from "@optimizely/optimizely-sdk";
import { Datafile, OptimizelyClient } from "../types";
import { logger as defaultLogger, Logger } from "@rea-argonaut/observability";

const emptyNotificationCenter: NotificationCenter = {
  addNotificationListener: () => -1,
  removeNotificationListener: () => false,
  clearAllNotificationListeners: () => {},
  clearNotificationListeners: () => {},
};
export const createEmptyOptimizelyClient = (
  logger: Logger = {
    log: () => {},
    error: () => {},
  },
): OptimizelyClient => ({
  createUserContext: (userId) => {
    logger.log(
      `OptimizelyClient.createUserContext was called with no datafile. userId=${userId}`,
    );
    return null;
  },
  getVariation: (experimentId, userId) => {
    logger.log(
      `OptimizelyClient.getVariation was called with no datafile. experimentId=${experimentId} userId=${userId}`,
    );
    return null;
  },
  activate: (experimentId, userId) => {
    logger.log(
      `OptimizelyClient.activate was called with no datafile. experimentId=${experimentId} userId=${userId}`,
    );
    return null;
  },
  track: (eventKey, userId) => {
    logger.log(
      `OptimizelyClient.track was called with no datafile. eventKey=${eventKey} userId=${userId}`,
    );
  },
  setForcedVariation: () => {
    return false;
  },
  getForcedVariation: () => {
    return null;
  },
  getOptimizelyConfig: () => {
    return null;
  },
  isFeatureEnabled: (featureId: string, userId: string) => {
    logger.log(
      `OptimizelyClient.isFeatureEnabled was called with no datafile. featureId=${featureId} userId=${userId}`,
    );
    return false;
  },
  getEnabledFeatures: () => {
    return [];
  },
  getFeatureVariable: () => null,
  getFeatureVariableBoolean: () => null,
  getFeatureVariableDouble: () => null,
  getFeatureVariableInteger: () => null,
  getFeatureVariableString: () => null,
  getFeatureVariableJSON: () => null,
  getAllFeatureVariables: (id: string, userId: string) => {
    logger.log(
      `OptimizelyClient.getAllFeatureVariables was called with no datafile. feature/experimentKey=${id} userId=${userId}`,
    );
    return null;
  },
  notificationCenter: emptyNotificationCenter,
  onReady: async () => {
    return {
      success: false,
      reason: "onReady called with no datafile present",
    };
  },
  close: async () => {
    return {
      success: false,
      reason: "close with no datafile present",
    };
  },
  getVuid: () => undefined,
  sendOdpEvent: () => {},
  getProjectConfig: () => null,
  isOdpIntegrated: () => false,
});

const optimizelyClientCache: Map<string, OptimizelyClient | null> = new Map();

export const getDatafileFromClientCache = (
  sdkKey?: string,
  clientCache: Map<string, OptimizelyClient | null> = optimizelyClientCache,
): string | undefined => {
  if (!sdkKey) {
    return;
  }
  return clientCache.get(sdkKey)?.getOptimizelyConfig()?.getDatafile();
};

const getCachedOptimizelyClient = (
  datafile: Datafile,
  cache: Map<string, OptimizelyClient | null>,
): OptimizelyClient | null => {
  const cacheKey = datafile.sdkKey;
  const cachedOptimizelyInstance = cache.get(cacheKey);

  if (cachedOptimizelyInstance) {
    return cachedOptimizelyInstance;
  }

  const newOptimizelyClient = optimizely.createInstance({
    sdkKey: datafile.sdkKey,
    odpOptions: { disabled: true },
    datafileOptions: { autoUpdate: true, updateInterval: 30_000 },
    datafile,
    eventBatchSize: 100,
  });

  cache.set(cacheKey, newOptimizelyClient);
  return newOptimizelyClient;
};
const createOptimizelyClient = (
  datafile?: Datafile,
  logger: Logger = defaultLogger,
  clientCache: Map<string, OptimizelyClient | null> = optimizelyClientCache,
): OptimizelyClient => {
  if (datafile) {
    optimizely.setLogger(null);
    const instance = getCachedOptimizelyClient(datafile, clientCache);
    if (instance) {
      return instance;
    }
  }

  return createEmptyOptimizelyClient(logger);
};

export default createOptimizelyClient;
